import type { ParseKeys } from 'i18next'

import type { Capability } from '@/types/domain'

/* The server's capability keys, mapped to the catalogue (QW-1, A-3).
 *
 * `GET /admin/capabilities` serves each key with an English description written
 * for the founder configuring a role (`backend/platform/authz/capabilities.go`).
 * That text is the server's, and a Russian role editor printed forty-five
 * English sentences under Russian headings. A key is a stable code, so the words
 * are chosen here by the layer that knows the reader's language, and the
 * server's description is only the fallback for a key this client does not know
 * yet — a server deployed ahead of the client still gets a readable checkbox.
 *
 * A flat map onto `settings.roles.capabilityLabels.<camelName>` rather than a
 * key tree mirroring the dots: the catalogue's `keySeparator` is the dot, and a
 * dotted capability key used as a path would be read as nesting.
 *
 * Deliberately not built on `CAP` (`lib/can.ts`): that is the set the client
 * GATES on, about two thirds of the registry. This is the whole registry,
 * because the role editor shows every key the server checks.
 * `capabilityLabels.test.ts` reads the Go registry and fails in both directions:
 * a registered key with no label here, and a label for a key nobody registers. */
export const CAPABILITY_LABELS: Readonly<Record<Capability, ParseKeys>> = {
  'quote.create': 'settings.roles.capabilityLabels.quoteCreate',
  'quote.send': 'settings.roles.capabilityLabels.quoteSend',
  'quote.team.assign': 'settings.roles.capabilityLabels.quoteTeamAssign',
  'quote.pricing.provide': 'settings.roles.capabilityLabels.quotePricingProvide',

  'case.create': 'settings.roles.capabilityLabels.caseCreate',
  'case.close.operational': 'settings.roles.capabilityLabels.caseCloseOperational',
  'case.close.financial': 'settings.roles.capabilityLabels.caseCloseFinancial',
  'case.route.edit': 'settings.roles.capabilityLabels.caseRouteEdit',
  'case.team.assign': 'settings.roles.capabilityLabels.caseTeamAssign',
  'case.reopen': 'settings.roles.capabilityLabels.caseReopen',
  'case.operations.lead': 'settings.roles.capabilityLabels.caseOperationsLead',

  'directory.client.create': 'settings.roles.capabilityLabels.directoryClientCreate',
  'directory.client.manage': 'settings.roles.capabilityLabels.directoryClientManage',
  'directory.transport.manage': 'settings.roles.capabilityLabels.directoryTransportManage',
  'directory.contact.read': 'settings.roles.capabilityLabels.directoryContactRead',

  'counterparty.read': 'settings.roles.capabilityLabels.counterpartyRead',
  'counterparty.create': 'settings.roles.capabilityLabels.counterpartyCreate',
  'counterparty.manage': 'settings.roles.capabilityLabels.counterpartyManage',

  'finance.expense.record': 'settings.roles.capabilityLabels.financeExpenseRecord',
  'finance.payment.record': 'settings.roles.capabilityLabels.financePaymentRecord',
  'finance.payment.client': 'settings.roles.capabilityLabels.financePaymentClient',
  'finance.receivables.read': 'settings.roles.capabilityLabels.financeReceivablesRead',
  'finance.payment.carrier': 'settings.roles.capabilityLabels.financePaymentCarrier',
  'finance.payment.confirm': 'settings.roles.capabilityLabels.financePaymentConfirm',
  'finance.rate.approve': 'settings.roles.capabilityLabels.financeRateApprove',
  'finance.rate.set': 'settings.roles.capabilityLabels.financeRateSet',

  'document.offer.render': 'settings.roles.capabilityLabels.documentOfferRender',
  'document.offer.issue': 'settings.roles.capabilityLabels.documentOfferIssue',
  'document.invoice.render': 'settings.roles.capabilityLabels.documentInvoiceRender',
  'document.invoice.issue': 'settings.roles.capabilityLabels.documentInvoiceIssue',
  'document.debitnote.render': 'settings.roles.capabilityLabels.documentDebitnoteRender',
  'document.debitnote.issue': 'settings.roles.capabilityLabels.documentDebitnoteIssue',
  'document.archive.read': 'settings.roles.capabilityLabels.documentArchiveRead',

  'collab.files.read': 'settings.roles.capabilityLabels.collabFilesRead',
  'collab.files.upload': 'settings.roles.capabilityLabels.collabFilesUpload',
  'collab.chat.post': 'settings.roles.capabilityLabels.collabChatPost',
  'collab.chat.delete': 'settings.roles.capabilityLabels.collabChatDelete',
  'collab.chat.read.all': 'settings.roles.capabilityLabels.collabChatReadAll',

  'route.template.manage': 'settings.roles.capabilityLabels.routeTemplateManage',

  'admin.users.read': 'settings.roles.capabilityLabels.adminUsersRead',
  'admin.users.manage': 'settings.roles.capabilityLabels.adminUsersManage',
  'admin.roles.read': 'settings.roles.capabilityLabels.adminRolesRead',
  'admin.roles.manage': 'settings.roles.capabilityLabels.adminRolesManage',
  'admin.settings.manage': 'settings.roles.capabilityLabels.adminSettingsManage',
  'admin.audit.read': 'settings.roles.capabilityLabels.adminAuditRead',
}

/** The catalogue key for one capability, or undefined for a key this client
 *  does not know — in which case the caller prints the server's description.
 *  `Object.hasOwn` rather than an index: `toString` is not a capability. */
export function capabilityLabelKey(capability: Capability): ParseKeys | undefined {
  return Object.hasOwn(CAPABILITY_LABELS, capability) ? CAPABILITY_LABELS[capability] : undefined
}

/** Capabilities grouped by area — the key's first dotted segment (`quote`,
 *  `case`, `directory`…), named from `settings.roles.groups`.
 *
 *  Areas come in alphabetical order of that segment; members keep the order
 *  they were given in, so the role editor still lists the catalogue as the
 *  server registers it. Derived from the KEY rather than a hand-kept list, so a
 *  capability added server-side lands in the right area without this file
 *  changing. Shared by the role editor and the user card's «Права роли» (S-7),
 *  so the two never group one role two ways. */
export function groupCapabilities(
  capabilities: readonly Capability[],
): Array<[area: string, members: Capability[]]> {
  const byArea = new Map<string, Capability[]>()
  for (const capability of capabilities) {
    const area = capability.split('.')[0] || capability
    const bucket = byArea.get(area) ?? []
    bucket.push(capability)
    byArea.set(area, bucket)
  }
  return [...byArea.entries()].sort(([a], [b]) => a.localeCompare(b))
}
